import { ChiroUpAPI } from '@chiroup/client-core/functions/ChiroUpAPI';
import {
  ConnectorCredentialVerdict,
  ConnectorRotationMetadata,
  ConnectorSecretStatus,
  ConnectorVendorStatus,
  UnroutedExchangeSummary,
} from '@chiroup/core/types/ConnectorAdmin.type';
import {
  ConnectorTraceConfig,
  ConnectorTraceFilters,
  ConnectorTraceRow,
} from '@chiroup/core/types/ConnectorTrace.type';
import { createConnectionApiMock } from './connectionApi.mock';

export type ConnectionApiHealth = {
  ok: boolean;
  service: string;
  stage: string;
  version: string;
};

/**
 * Ops-console client for connection-api (plan §3g). The `connectionApi`
 * Amplify REST entry is registered in main.tsx; the admin id token rides
 * every call automatically.
 */
const connectionApiService = () => {
  const vendorPath = (vendor: string, integrationId: number) =>
    `/connector/credentials/${encodeURIComponent(vendor)}/${integrationId}`;
  const tracePath = (vendor: string, integrationId: number) =>
    `/connector/trace/${encodeURIComponent(vendor)}/${integrationId}`;

  const health = (): Promise<ConnectionApiHealth> => {
    return ChiroUpAPI.get('connectionApi', '/health', {});
  };

  const vendors = (): Promise<{ vendors: ConnectorVendorStatus[] }> => {
    return ChiroUpAPI.get('connectionApi', '/connector/vendors', {});
  };

  const runCredentialCheck = (
    vendor?: string
  ): Promise<{
    results: Record<string, ConnectorCredentialVerdict>;
  }> => {
    return ChiroUpAPI.post('connectionApi', '/connector/credential-check/run', {
      body: vendor ? { vendor } : {},
    });
  };

  const stageCandidate = (
    vendor: string,
    integrationId: number,
    apiKey: string
  ): Promise<{ status: ConnectorSecretStatus; rotation: ConnectorRotationMetadata }> => {
    return ChiroUpAPI.post('connectionApi', `${vendorPath(vendor, integrationId)}/candidate`, {
      body: { apiKey },
    });
  };

  const validateCandidate = (
    vendor: string,
    integrationId: number
  ): Promise<{ valid: boolean; rotation: ConnectorRotationMetadata }> => {
    return ChiroUpAPI.post(
      'connectionApi',
      `${vendorPath(vendor, integrationId)}/validate-candidate`,
      { body: {} }
    );
  };

  const promoteCandidate = (
    vendor: string,
    integrationId: number
  ): Promise<{ status: ConnectorSecretStatus; rotation: ConnectorRotationMetadata }> => {
    return ChiroUpAPI.post('connectionApi', `${vendorPath(vendor, integrationId)}/promote`, {
      body: {},
    });
  };

  const rollback = (
    vendor: string,
    integrationId: number
  ): Promise<{ status: ConnectorSecretStatus; rotation: ConnectorRotationMetadata }> => {
    return ChiroUpAPI.post('connectionApi', `${vendorPath(vendor, integrationId)}/rollback`, {
      body: {},
    });
  };

  const traceConfig = (
    vendor: string,
    integrationId: number
  ): Promise<{ config: ConnectorTraceConfig | null; active: boolean }> => {
    return ChiroUpAPI.get('connectionApi', tracePath(vendor, integrationId), {});
  };

  const setTraceConfig = (
    vendor: string,
    integrationId: number,
    body: { filters?: ConnectorTraceFilters; durationMinutes: number }
  ): Promise<{ config: ConnectorTraceConfig; active: boolean }> => {
    return ChiroUpAPI.post('connectionApi', tracePath(vendor, integrationId), { body });
  };

  const traceLogs = (
    vendor: string,
    integrationId: number,
    params: { sinceId?: number; limit?: number }
  ): Promise<{ rows: ConnectorTraceRow[] }> => {
    return ChiroUpAPI.get('connectionApi', `${tracePath(vendor, integrationId)}/logs`, {
      queryParams: {
        sinceId: String(params.sinceId ?? 0),
        limit: String(params.limit ?? 100),
      },
    });
  };

  const stampExchange = (
    exchangeId: string,
    durationMinutes: number
  ): Promise<{ exchangeId: string; traceUntil: number }> => {
    return ChiroUpAPI.post(
      'connectionApi',
      `/connector/trace-exchange/${encodeURIComponent(exchangeId)}`,
      { body: { durationMinutes } }
    );
  };

  // ── Unrouted queue (plan §3e.7) — endpoints land with INFRA-5; the UI is
  //    built ahead against the shared UnroutedExchangeSummary contract. ──────
  const listUnrouted = (): Promise<{ rows: UnroutedExchangeSummary[] }> => {
    return ChiroUpAPI.get('connectionApi', '/connector/unrouted', {});
  };

  const assignUnrouted = (
    exchangeId: string,
    clinicId: number
  ): Promise<{ exchangeId: string; clinicId: number }> => {
    return ChiroUpAPI.post(
      'connectionApi',
      `/connector/unrouted/${encodeURIComponent(exchangeId)}/assign`,
      { body: { clinicId } }
    );
  };

  const closeUnrouted = (exchangeId: string, reason: string): Promise<{ exchangeId: string }> => {
    return ChiroUpAPI.post(
      'connectionApi',
      `/connector/unrouted/${encodeURIComponent(exchangeId)}/close`,
      { body: { reason } }
    );
  };

  return {
    health,
    vendors,
    runCredentialCheck,
    stageCandidate,
    validateCandidate,
    promoteCandidate,
    rollback,
    traceConfig,
    setTraceConfig,
    traceLogs,
    stampExchange,
    listUnrouted,
    assignUnrouted,
    closeUnrouted,
  };
};

export type ConnectionApiService = ReturnType<typeof connectionApiService>;

/**
 * DEV-ONLY escape hatch to exercise the ops console UI without a backend:
 * append `?use-case=<scenario>` to the page URL (evaluated once at page
 * load). The single `import.meta.env.DEV && ...` expression is statically
 * false in production builds, so the mock factory reference — and with it
 * the whole mock module — is tree-shaken out of the shipped bundle.
 */
const service: ConnectionApiService =
  import.meta.env.DEV && new URLSearchParams(window.location.search).get('use-case')
    ? createConnectionApiMock()
    : connectionApiService();

export default service;
